import type { User } from '../../scenes/authentication/types/types.auth';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import NavbarCompanySearch from '@shared/components/NavbarCompanySearch';
import UserAvatar from '@shared/components/UserAvatar';
import { useResponsive } from '@shared/hooks/useResponsive';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import kandirLogo from '../../assets/images/kandir_logo_blue.svg';
import SignUpOrLogInModal from '../../scenes/authentication/components/SignUpOrLogInModal';
import { useReviewContext } from '../../scenes/review/context/ReviewContext';
import { UserContext } from '../contexts/UserContext';
import SideDrawer, { shouldHideDrawer } from './SideDrawer';

export interface NavBarProps {
  isNavOpen: boolean;
  setIsNavOpen: (isOpen: boolean) => void;
  transparent: boolean;
}

function Navbar(props: NavBarProps) {
  const { setIsNavOpen, isNavOpen } = props;
  const { user, logout } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'signup' | 'login'>('signup');

  // Check if review dialog is open
  let isReviewDialogOpen = false;
  try {
    const reviewContext = useReviewContext();
    isReviewDialogOpen = reviewContext.isReviewDialogOpen;
  }
  catch {
    // ReviewContext not available, no review dialog open
    isReviewDialogOpen = false;
  }

  const isOnboarding = location.pathname === '/onboarding';

  useEffect(() => {
    setAnchorEl(null);
  }, [user]);

  const handleAccountClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleGoHome = () => {
    navigate('/my-reviews');
    handleMenuClose();
  };

  const handleGoSettings = () => {
    navigate('/account-settings');
    handleMenuClose();
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleFeedback = useCallback(async () => {
    const feedback = Sentry.getFeedback();
    const form = await feedback?.createForm();
    if (form) {
      form.appendToDom();
      form.open();
    }
  }, []);

  const AUTH_ROUTES = ['/sign-in', '/sign-up', '/verification', '/forgot-password', '/get-started', '/'];

  const isAuthRoute = (path: string) =>
    AUTH_ROUTES.some(
      (route) =>
        path === route || path.startsWith(`${route}/`) || path.startsWith(`${route}?`),
    );

  const stashPreLoginPath = () => {
    // Don't bounce a successful modal sign-in back to the auth page the
    // user opened the modal from — store the path only when it's a real
    // destination they want to return to.
    if (!isAuthRoute(location.pathname)) {
      sessionStorage.setItem('preLoginPath', location.pathname + location.search);
    }
    else {
      sessionStorage.removeItem('preLoginPath');
    }
  };

  const handleSignUpClick = () => {
    stashPreLoginPath();
    setAuthModalMode('signup');
    setAuthModalOpen(true);
  };

  const handleLogInClick = () => {
    stashPreLoginPath();
    setAuthModalMode('login');
    setAuthModalOpen(true);
  };

  const handleAuthModalClose = () => {
    setAuthModalOpen(false);
  };

  const handleAuthSuccess = (signedInUser?: User) => {
    const storedPath = sessionStorage.getItem('preLoginPath');
    const needsOnboarding = signedInUser && !signedInUser.networkOnboardingComplete;
    const safeStoredPath = storedPath && !isAuthRoute(storedPath) ? storedPath : null;
    const redirectPath = needsOnboarding
      ? '/onboarding'
      : safeStoredPath || '/my-reviews';
    sessionStorage.removeItem('preLoginPath');
    setAuthModalOpen(false);
    navigate(redirectPath);
  };

  const hideDrawer = shouldHideDrawer(location.pathname);

  return (
    <>
      <Box role="app-nav" className="navbar">
        {user && (
          <IconButton
            onClick={() => setIsNavOpen(!isNavOpen)}
            className={
              isNavOpen || hideDrawer
                ? 'navbar__menu-button navbar__menu-button--hidden'
                : 'navbar__menu-button'
            }
          >
            <MenuIcon fontSize="large" />
          </IconButton>
        )}

        {(!isNavOpen || hideDrawer || !user) && !isMobile && (
          <Box
            component="img"
            src={kandirLogo}
            alt="Kandir Logo"
            className="navbar__logo"
          />
        )}

        <Box className="navbar__right-section">
          {!isMobile && !isOnboarding && (
            <Box
              className={
                isReviewDialogOpen
                  ? 'navbar__search-container navbar__search-container--hidden'
                  : 'navbar__search-container'
              }
            >
              <NavbarCompanySearch />
            </Box>
          )}

          {user
            ? (
                <>
                  {!isMobile && !isOnboarding && !isReviewDialogOpen && (
                    <Button
                      variant="contained"
                      onClick={handleFeedback}
                      className="button-rounded button-rounded--solid-white"
                    >
                      Give Feedback
                    </Button>
                  )}

                  {!isOnboarding && !isReviewDialogOpen && (
                    <Tooltip title={`Logged in as ${user.email}`}>
                      <IconButton
                        onClick={handleAccountClick}
                        className="navbar__avatar-button"
                      >
                        <UserAvatar
                          firstName={user.firstName}
                          lastName={user.lastName}
                          username={user.username}
                          avatarColor={user.avatarColor}
                          size={40}
                        />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    slotProps={{
                      paper: {
                        className: 'navbar-menu__paper',
                      },
                      list: {
                        className: 'navbar-menu__list',
                      },
                    }}
                  >
                    <MenuItem onClick={handleGoHome} className="navbar-menu__item">
                      <HomeIcon className="navbar-menu__icon" />
                      Home
                    </MenuItem>
                    <MenuItem onClick={handleGoSettings} className="navbar-menu__item">
                      <SettingsIcon className="navbar-menu__icon" />
                      Settings
                    </MenuItem>
                    <MenuItem onClick={handleLogout} className="navbar-menu__item navbar-menu__item--logout">
                      <LogoutIcon className="navbar-menu__icon" />
                      Logout
                    </MenuItem>
                  </Menu>
                </>
              )
            : (
                <Box className="navbar__auth-buttons">
                  <Button
                    variant="contained"
                    onClick={handleLogInClick}
                    className="button-rounded button-rounded--solid-white"
                  >
                    Log In
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSignUpClick}
                    className="button-rounded button-rounded--primary"
                  >
                    Sign Up
                  </Button>
                </Box>
              )}
        </Box>
      </Box>

      <SideDrawer isOpen={isNavOpen} setIsOpen={setIsNavOpen} />

      <SignUpOrLogInModal
        open={authModalOpen}
        onClose={handleAuthModalClose}
        initialMode={authModalMode}
        handleAuthSuccess={handleAuthSuccess}
      />
    </>
  );
}

export default Navbar;
